/**
 * SYNTHETIC rate generator - the fallback behind fixture mode.
 *
 * Only one lane in this kit has a genuine recorded carrier response
 * (LOAD-004192, see fixtures/). For every other load, fixture mode synthesises a
 * rate set here so the demo still runs with no token, no network and no VPN.
 *
 * These are MODELLED numbers, not carrier numbers. Everything this module
 * produces is stamped `rateSource: "synthetic"` and the UI badges it, so a
 * modelled rate can never be mistaken on screen for one the carrier returned.
 *
 * The constants below were back-solved from the single recorded response so the
 * shape of the output is plausible - discount ladders steepening with service
 * level, DIM weight dominating on light-but-bulky freight, fuel riding at ~9% of
 * the discounted total. They are not a carrier tariff and must never be quoted
 * to a customer.
 */

// --- Constants back-solved from the recorded Scottsdale -> Cave Creek response.
// Ground billed 128.6 lb and express 138.0 lb on the same 27,000 cu in package,
// which implies these effective divisors rather than the published 139.
const DIM_DIVISOR_GROUND = 209.95;
const DIM_DIVISOR_EXPRESS = 195.65;

/**
 * Zone 2 base charge as `FLOOR + K * billedWeight^0.85`.
 *
 * Parcel tariffs are strongly concave - a 20 lb package costs far more per pound
 * than a 130 lb one, and there is always a minimum charge underneath. A flat
 * per-pound rate gets the reference load right and then quotes absurd sub-$15
 * totals for light freight, so the curve and the floor both matter.
 *
 * K is solved so each service reproduces its recorded base charge exactly at the
 * reference weight (128.6 lb ground / 138.0 lb express, zone 2).
 */
const WEIGHT_EXPONENT = 0.85;
const BASE_CURVE = {
  F92: { floor: 12.0, k: 1.4709 }, // Ground             -> 103.27 @ 128.6 lb
  F94: { floor: 12.5, k: 1.5499 }, // Home Delivery
  F20: { floor: 22.0, k: 2.2635 }, // Express Saver
  F3: { floor: 30.0, k: 3.7132 }, //  2Day               -> 274.62 @ 138.0 lb
  F2: { floor: 34.0, k: 4.3227 }, //  2Day A.M.
  F5: { floor: 40.0, k: 4.8811 }, //  Standard Overnight
  F1: { floor: 45.0, k: 5.7686 }, //  Priority Overnight -> 425.04 @ 138.0 lb
};

/** Negotiated discount off base, by service. Steepens with service level. */
const DISCOUNT_RATE = {
  F92: 0.701,
  F94: 0.690,
  F20: 0.762,
  F3: 0.815,
  F2: 0.828,
  F5: 0.851,
  F1: 0.874,
};

/** Published-list multipliers: list base / net base, and list SS / net SS. */
const LIST_BASE_MULT = { F92: 1.526, F94: 1.520, F20: 1.440, F3: 1.413, F2: 1.413, F5: 1.413, F1: 1.413 };
const LIST_SS_MULT = { F92: 6.77, F94: 6.90, F20: 7.60, F3: 8.21, F2: 8.60, F5: 9.00, F1: 9.36 };

const SERVICES = [
  { code: 'F92', name: 'FedEx Ground®', commit: '11:59 PM', express: false },
  { code: 'F94', name: 'FedEx Home Delivery®', commit: '8:00 PM', express: false, residentialOnly: true },
  { code: 'F20', name: 'FedEx Express Saver®', commit: '4:30 PM', express: true, days: 3 },
  { code: 'F3', name: 'FedEx 2Day®', commit: '5:00 PM', express: true, days: 2 },
  { code: 'F2', name: 'FedEx 2Day® A.M.', commit: '10:30 AM', express: true, days: 2 },
  { code: 'F5', name: 'FedEx Standard Overnight®', commit: '4:30 PM', express: true, days: 1 },
  { code: 'F1', name: 'FedEx Priority Overnight®', commit: '5:00 PM', express: true, days: 1 },
];

/** Rough state centroids - good enough to derive a defensible zone. */
const STATE_CENTROID = {
  AL: [32.8, -86.8], AK: [64.0, -152.0], AZ: [34.2, -111.7], AR: [34.9, -92.4],
  CA: [37.2, -119.5], CO: [39.0, -105.5], CT: [41.6, -72.7], DE: [39.0, -75.5],
  DC: [38.9, -77.0], FL: [28.6, -82.4], GA: [32.6, -83.4], HI: [20.3, -156.4],
  ID: [44.4, -114.6], IL: [40.0, -89.2], IN: [39.9, -86.3], IA: [42.1, -93.5],
  KS: [38.5, -98.4], KY: [37.5, -85.3], LA: [31.1, -92.0], ME: [45.4, -69.2],
  MD: [39.0, -76.8], MA: [42.3, -71.8], MI: [44.3, -85.4], MN: [46.3, -94.3],
  MS: [32.7, -89.7], MO: [38.4, -92.5], MT: [47.0, -109.6], NE: [41.5, -99.8],
  NV: [39.3, -116.6], NH: [43.7, -71.6], NJ: [40.2, -74.7], NM: [34.4, -106.1],
  NY: [42.9, -75.5], NC: [35.5, -79.4], ND: [47.4, -100.5], OH: [40.3, -82.8],
  OK: [35.6, -97.5], OR: [43.9, -120.6], PA: [40.9, -77.8], RI: [41.7, -71.6],
  SC: [33.9, -80.9], SD: [44.4, -100.2], TN: [35.9, -86.4], TX: [31.5, -99.3],
  UT: [39.3, -111.7], VT: [44.1, -72.7], VA: [37.5, -78.9], WA: [47.4, -120.5],
  WV: [38.6, -80.6], WI: [44.6, -89.7], WY: [43.0, -107.6],
};

function haversineMiles(a, b) {
  if (!a || !b) return 800;
  const toRad = (d) => (d * Math.PI) / 180;
  const [lat1, lon1] = a;
  const [lat2, lon2] = b;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 3958.8 * 2 * Math.asin(Math.sqrt(h));
}

function zoneFor(originState, destState) {
  const miles = haversineMiles(STATE_CENTROID[originState], STATE_CENTROID[destState]);
  if (miles < 150) return 2;
  if (miles < 300) return 3;
  if (miles < 600) return 4;
  if (miles < 1000) return 5;
  if (miles < 1400) return 6;
  if (miles < 1800) return 7;
  return 8;
}

/** Ground transit days grow with zone; express is fixed by product. */
function groundTransitDays(zone) {
  return Math.min(6, Math.max(1, zone - 1));
}

/** Stable per-load jitter so a rebuild and a re-rate show identical numbers. */
function seededJitter(key, spread) {
  let h = 2166136261;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  const unit = ((h >>> 0) % 10000) / 10000; // 0..1
  return 1 + (unit - 0.5) * 2 * spread;
}

function money(n) {
  return Math.round(n * 100) / 100;
}

/**
 * Adds transit days across business days only. Applies to express as well as
 * ground - a two-day service tendered on Thursday lands Monday, not Saturday.
 */
function deliveryDate(shipIso, days) {
  const d = new Date(`${shipIso}T00:00:00Z`);
  let remaining = days;
  while (remaining > 0) {
    d.setUTCDate(d.getUTCDate() + 1);
    const dow = d.getUTCDay();
    if (dow !== 0 && dow !== 6) remaining--;
  }
  return d;
}

function fmtUS(date) {
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  return `${mm}/${dd}/${date.getUTCFullYear()}`;
}

/**
 * Produces a RateShop-shaped response for a load, in the same envelope the live
 * API returns so the normaliser downstream cannot tell the difference in shape.
 */
export function synthesiseRateShop(load) {
  const pkg = load.package;
  const cube = pkg.length * pkg.width * pkg.height;
  const longest = Math.max(pkg.length, pkg.width, pkg.height);
  const girth = 2 * (pkg.width + pkg.height);
  const lengthPlusGirth = longest + girth;

  const zone = zoneFor(load.warehouse.state, load.shipTo.state);
  const zoneFactor = 1 + (zone - 2) * 0.125;

  // Oversize mirrors the recorded response: a 36x30x25 package drew ~$54.48.
  const isOversize = longest > 30 || lengthPlusGirth > 130 || cube > 20000;
  const isOverlength = longest > 48;

  const results = SERVICES.filter((svc) => {
    if (svc.residentialOnly && !load.residential) return false;
    if (svc.code === 'F92' && load.residential) return false; // Home Delivery replaces Ground
    return true;
  }).map((svc) => {
    const divisor = svc.express ? DIM_DIVISOR_EXPRESS : DIM_DIVISOR_GROUND;
    const dimWeight = cube / divisor;
    const billedWeight = money(Math.max(pkg.weight, dimWeight));
    const dimApplied = dimWeight > pkg.weight;

    const jitter = seededJitter(`${load.loadId}:${svc.code}`, 0.03);
    const curve = BASE_CURVE[svc.code];
    const baseCharge = money(
      (curve.floor + curve.k * billedWeight ** WEIGHT_EXPONENT) * zoneFactor * jitter,
    );
    const discount = money(-baseCharge * DISCOUNT_RATE[svc.code]);
    const netBase = money(baseCharge + discount);

    const charges = [
      { ID: 'BASE CHG', Value: baseCharge },
      { ID: 'DISCOUNT CHG', Value: discount },
    ];

    // Extended-area surcharge shows up on outlying delivery zips.
    const extArea = money(0.61 * seededJitter(`${load.loadId}:ext`, 0.05));
    charges.push({ ID: 'EXT AREA CHG', Value: extArea });

    let oversize = 0;
    if (isOversize) {
      oversize = money(54.48 * seededJitter(`${load.loadId}:ovr`, 0.02));
      charges.push({ ID: 'OVERSIZE CHG', Value: oversize });
    }

    let overlength = 0;
    if (isOverlength) {
      overlength = money(98.5 * seededJitter(`${load.loadId}:ovl`, 0.03));
      charges.push({ ID: 'OVERLENGTH CHG', Value: overlength });
    }

    let residential = 0;
    if (load.residential) {
      residential = money(6.35 * seededJitter(`${load.loadId}:res`, 0.02));
      charges.push({ ID: 'RESIDENTIAL CHG', Value: residential });
    }

    const ssCharge = money(extArea + oversize + overlength + residential);
    const fuelCharge = money((netBase + ssCharge) * 0.09);
    charges.push({ ID: 'FUEL CHG', Value: fuelCharge });

    const total = money(netBase + ssCharge + fuelCharge);

    const listBase = money(baseCharge * LIST_BASE_MULT[svc.code]);
    const listSS = money(ssCharge * LIST_SS_MULT[svc.code]);

    const days = svc.express ? svc.days : groundTransitDays(zone);
    const delivery = deliveryDate(load.shipDate, days);
    const zoneStr = svc.express ? String(zone).padStart(2, '0') : String(zone);

    return {
      ActDeliveryDate: fmtUS(delivery),
      ActTNTDays: String(days),
      ErrorDesc: 'Success',
      ErrorNum: 0,
      Rate: total,
      RateEngine: 'FDXWS',
      ServiceDescription: svc.name,
      ShipVia: svc.code,
      TNTDays: String(days),
      TNTTime: svc.commit,
      Zone: zoneStr,
      CarrierType: 'PARCEL',
      CarrierField4: 'ShopComplete',
      IsAuction: false,
      Charges: charges,
      Shipment: {
        Summary: {
          ActualWeight: pkg.weight,
          BilledWeight: billedWeight,
          BaseCharge: baseCharge,
          SSCharge: ssCharge,
          FuelCharge: fuelCharge,
          Discount: discount,
          TotalCharge: total,
          TNTDays: String(days),
          TNTTime: svc.commit,
        },
        ShipVia: {
          ShipVia: svc.code,
          CarrierCode: 'FDX',
          CarrierName: 'FedEx',
          CarrierType: 'PARCEL',
          Description: svc.name,
          ServiceName: svc.name,
          SCAC: 'FDX',
        },
        Packages: [
          {
            PackageID: 1,
            ActualWeight: pkg.weight,
            BilledWeight: billedWeight,
            Charge: total,
            Length: longest,
            Width: pkg.width,
            Height: pkg.height,
            Charges: charges,
            ListCharges: [
              { ID: 'LST BASE CHG', Value: listBase },
              { ID: 'LST SS CHG', Value: listSS },
              { ID: 'LST TOTAL CHG', Value: money(listBase + listSS) },
            ],
            Attributes: [
              { ID: 'RateEngine', Value: 'FDXWS' },
              { ID: 'DIM FLAG', Value: dimApplied ? 'Y' : 'N' },
              { ID: 'DIMWeight', Value: money(dimWeight).toFixed(1) },
              { ID: 'Zone', Value: zoneStr },
              { ID: 'TNTDays', Value: String(days) },
              { ID: 'TNTTime', Value: svc.commit },
              { ID: 'RateDiscountType', Value: 'VOLUME' },
            ],
          },
        ],
        Attributes: [
          // RateLinx echoes its client location id here, not the shipper's name -
          // 'RSM' matches the recorded fixture regardless of who the origin address is.
          { ID: 'ClientLocID', Value: 'RSM' },
          { ID: 'FDXWSAddrType', Value: load.residential ? 'RESIDENTIAL' : 'BUSINESS' },
        ],
        BillingType: '1',
        Engine: 'LRMT',
        ErrorNum: 0,
        RequiredByDate: load.requiredByDateRateLinx,
        ResultDesc: 'Success',
        ShipDate: load.shipDateRateLinx,
      },
    };
  });

  // Cheapest first, matching how the live engine tends to order a completed shop.
  results.sort((a, b) => a.Rate - b.Rate);

  return {
    ResultStatus: 'Success',
    ResultData: { Results: results },
  };
}
